import { generateAIText } from "@/lib/ai-text";
import { getDefaultProvider, type ProviderType } from "@/ai/providers";
import { getRecommendedModel } from "@/ai/models";

export interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
  correctIndex: number;
  explanation: string;
  points: number;
}

export interface GeneratedQuiz {
  title: string;
  description: string;
  questions: QuizQuestion[];
}

function normalizeJsonResponse(raw: string): string {
  let text = raw.trim();

  // Strip Markdown fences if present
  if (text.startsWith("```")) {
    const firstFenceEnd = text.indexOf("\n");
    if (firstFenceEnd !== -1) {
      text = text.slice(firstFenceEnd + 1);
    }
    const lastFenceStart = text.lastIndexOf("```");
    if (lastFenceStart !== -1) {
      text = text.slice(0, lastFenceStart).trim();
    }
  }

  // Sometimes models wrap the JSON in prose; try to find first { and last }
  const firstBrace = text.indexOf("{");
  const lastBrace = text.lastIndexOf("}");
  if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
    text = text.slice(firstBrace, lastBrace + 1);
  }

  return text;
}

export async function generatePolicyQuiz(options: {
  policyTitle: string;
  policyContent: string;
  companyName?: string;
  providerOverride?: ProviderType;
}): Promise<GeneratedQuiz> {
  const { policyTitle, policyContent, companyName, providerOverride } = options;

  const provider = providerOverride ?? getDefaultProvider();
  const model = getRecommendedModel(provider, "QUIZ");

  const systemPrompt = [
    "You are an expert compliance trainer designing engaging, flashcard-style quizzes for employees.",
    "Create a concise quiz that checks understanding of the policy while staying practical and friendly.",
    "The quiz will be shown one question at a time in a Duolingo-style UI.",
  ]
    .filter(Boolean)
    .join(" ");

  const userPrompt = [
    `Company: ${companyName || "Our company"}.`,
    `Policy title: "${policyTitle}".`,
    "",
    "Policy content:",
    policyContent.slice(0, 12000), // safety guard
    "",
    "Generate a JSON object with this exact shape:",
    "{",
    '  "title": string,',
    '  "description": string,',
    '  "questions": [',
    "    {",
    '      "id": string,',
    '      "question": string,',
    '      "options": [string, string, string, string],',
    '      "correctIndex": number, // 0-based index into options',
    '      "explanation": string,',
    '      "points": number // typically 10',
    "    },",
    "    // ... around 8-12 total questions",
    "  ]",
    "}",
    "",
    "Requirements:",
    "- Only return JSON, no markdown, no comments, no backticks.",
    "- Questions must be clear, jargon-free, and test key ideas from the policy.",
    "- Each question should have exactly 4 options with one clearly correct answer.",
    "- Use friendly, encouraging language in explanations.",
  ].join("\n");

  const raw = await generateAIText({
    provider,
    model,
    system: systemPrompt,
    messages: [
      {
        role: "user",
        content: userPrompt,
      },
    ],
    maxTokens: 2000,
    temperature: 0.4,
  });

  const normalized = normalizeJsonResponse(raw);

  let parsed: any;
  try {
    parsed = JSON.parse(normalized);
  } catch (error) {
    console.error("Failed to parse quiz JSON from AI:", error, normalized);
    throw new Error("The AI response for quiz generation was not valid JSON.");
  }

  if (
    !parsed ||
    typeof parsed !== "object" ||
    !Array.isArray(parsed.questions) ||
    parsed.questions.length === 0
  ) {
    throw new Error("The AI did not return a valid quiz structure.");
  }

  const questions: QuizQuestion[] = parsed.questions.map((q: any, index: number) => ({
    id: typeof q.id === "string" && q.id.length > 0 ? q.id : `q_${index + 1}`,
    question: String(q.question ?? ""),
    options: Array.isArray(q.options) ? q.options.map((opt: any) => String(opt)) : [],
    correctIndex: typeof q.correctIndex === "number" ? q.correctIndex : 0,
    explanation: String(q.explanation ?? ""),
    points:
      typeof q.points === "number" && Number.isFinite(q.points) && q.points > 0
        ? q.points
        : 10,
  }));

  if (questions.some((q) => q.options.length !== 4)) {
    throw new Error("Each quiz question must have exactly 4 answer options.");
  }

  const quiz: GeneratedQuiz = {
    title: String(parsed.title || `${policyTitle} — Knowledge Check`),
    description: String(
      parsed.description ||
        `Quick quiz to reinforce understanding of the "${policyTitle}" policy.`
    ),
    questions,
  };

  return quiz;
}

